/*
 * @fileOverView Filters Vuex module definition
 *
 * Handles dynamic filtering functionality within list components.
 */

import axios from 'axios';

export default {
  namespaced: true,
  state: {
    externalValues: {},
    // An array of objects to support multiple filter sets on the same page.
    sets: {}
  },

  mutations: {
    /**
     * Creates a filter set for the given ID.
     */
    createInstance(state, id) {
      state.sets[id] = {
        count: false,
        endpoint: '',
        id: id,
        loading: false,
        locked: false,
        values: {}
      };
    },

    /**
     * Set the number of results within the filtered data.
     */
    setCount(state, payload) {
      state.sets[payload.id].count = payload.count;
    },

    /**
     * Set the list's endpoint to query for filtered data.
     */
    setEndpoint(state, payload) {
      state.sets[payload.id].endpoint = payload.endpoint;
    },

    /**
     * Set whether or not the list is currently requesting filtered data.
     *
     * Used to display the loading state to the end user.
     */
    setLoading(state, payload) {
      state.sets[payload.id].loading = payload.loading;
    },

    /**
     * Set whether or not a changed filter should trigger a new request.
     *
     * By default, a changed filter triggers a new request to immediately update
     * the results. But in some cases, such as loading a page with multiple URL
     * parameters or clearing all filters at once, many filters are changed
     * quickly and the results shouldn't be requested until they've all changed.
     * Whatever is performing such an action is responsible for setting locked
     * to true, changing the filters, then reseting it and dispatching update.
     */
    setLocked(state, payload) {
      state.sets[payload.id].locked = payload.locked;
    },

    /**
     * Set the value for an individual filter.
     */
    setValue(state, payload) {
      if (payload.value === '' || !payload.value) {
        if (typeof state.sets[payload.id].values[payload.name] !== 'undefined') {
          // Values should only be tracked when they're not empty.
          delete state.sets[payload.id].values[payload.name];
        }
      }
      else {
        state.sets[payload.id].values[payload.name] = payload.value;
      }
    },

    /**
     * Set the value for an external element, such as a location search.
     */
    setExternalValue(state, payload) {
      state.externalValues[payload.name] = payload.value;
    }
  },

  actions: {
    /**
     * Updates the list's endpoint.
     *
     * Exists because the setEndpoint mutation can't commit another mutation.
     */
    setEndpoint({ state, commit }, payload) {
      const endpointPieces = payload.endpoint.split('?');

      commit('setEndpoint', {
        id: payload.id,
        endpoint: endpointPieces.shift()
      });

      // If the original endpoint already has a query, the values of the
      // parameters are immediately assigned as filter values. One place this is
      // used is within the site's search functionality.
      if (endpointPieces.length) {
        const endpointQuery = endpointPieces[0].split('=');

        commit('setValue', {
          id: payload.id,
          name: endpointQuery[0],
          value: endpointQuery[1]
        });
      }
    },

    /**
     * Updates a filter's value.
     */
    setValue({ state, commit, dispatch }, payload) {
      commit('setValue', payload);

      // If the list isn't locked, immediately request the new set of results.
      if (!state.locked) {
        dispatch('update', payload.id);
      }
    },

    /**
     * Updates an external element's value.
     *
     * "External" denotes an element that contributes to filtering, but falls
     * outside of the primary filters-vue component. An example is the location
     * search on MJFF Near Me. They are kept separate because their values
     * shouldn't fall into standard behavior, such as chips and clearing.
     */
    setExternalValue({ state, commit, dispatch }, payload) {
      commit('setExternalValue', payload);

      if (!state.locked) {
        dispatch('updateAll');
      }
    },

    /**
     * Updates the results for all filter sets on the page.
     */
    updateAll({ state, commit, dispatch }) {
      for (var setId in state.sets) {
        if (state.sets.hasOwnProperty(setId)) {
          dispatch('update', setId);
        }
      }
    },

    /**
     * Updates the results given the selected filters.
     */
    update({ state, commit }, id) {
      // Build the query from the selected filter values.
      const queryValues = {
        ...state.sets[id].values,
        ...state.externalValues
      };
      const query = Object.keys(queryValues).map(key => key + '=' + encodeURIComponent(queryValues[key])).join('&');

      // Display feedback to the user.
      commit('setLoading', {
        id: id,
        loading: true
      });

      axios.get(`${state.sets[id].endpoint}?${query}`)
        .then(response => {
          // Replace the list markup with what the endpoint returned.
          const list = document.getElementById(id);
          list.innerHTML = response.data.markup;

          // Update the result count.
          commit('setCount', {
            id: id,
            count: response.data.result_count
          });

          // Reinitialize Ajax behavior on any Views markup added to the page.
          if (typeof Drupal !== 'undefined') {
            const pager = list.querySelector('[rel="next"]');
            const jsSettings = response.data.js_settings;

            if (pager && jsSettings) {
              pager.setAttribute('href', jsSettings.view_path + '?page=1');
              drupalSettings.views.ajaxViews['views_dom_id:' + jsSettings.view_dom_id] = jsSettings;
              Drupal.behaviors.ViewsAjaxView.attach(list, drupalSettings);
            }
          }

          commit('setLoading', {
            id: id,
            loading: false
          });
        })
        .catch(error => {
          console.log(error);
        });
    }
  }
};
